'use client';

import { motion } from 'framer-motion';
import { useBuilderStore } from '@/store/builderStore';
import Button from '@/app/components/ui/Button';
import { RotateCcw, CheckCircle2 } from 'lucide-react';
import { useEffect } from 'react';

export default function Success() {
  const { resetBuilder } = useBuilderStore();

  useEffect(() => {
    // Dynamically import canvas-confetti to avoid SSR issues
    import('canvas-confetti').then((mod) => {
      const confetti = mod.default;
      const fire = () => {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#f59e0b', '#d97706', '#fbbf24', '#1a1a2e', '#ffffff'],
        });
      };
      const timer = setTimeout(() => {
        fire();
        setTimeout(fire, 400);
      }, 300);
      return () => clearTimeout(timer);
    });
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="flex flex-col items-center justify-center min-h-[60vh] text-center px-8 py-16"
    >
      {/* Animated checkmark */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20,
          delay: 0.1,
        }}
        className="relative mb-8"
      >
        {/* Outer glow ring */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="absolute inset-0 rounded-full bg-emerald-400/20 scale-150 animate-pulse"
        />

        {/* Inner ring */}
        <div className="w-28 h-28 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-2xl shadow-emerald-500/40">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 500, damping: 25, delay: 0.2 }}
          >
            <CheckCircle2 size={56} className="text-white" strokeWidth={2} />
          </motion.div>
        </div>
      </motion.div>

      {/* Text content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="max-w-md"
      >
        <h2 className="text-3xl font-black text-slate-800 mb-3 tracking-tight">
          Thank You!
        </h2>
        <p className="text-lg text-amber-600 font-bold mb-4">
          Your enquiry has been sent successfully.
        </p>
        <p className="text-slate-500 leading-relaxed mb-2">
          Our sales team will review your product inquiry and contact you shortly with a detailed
          quotation.
        </p>
        <p className="text-sm text-slate-400">
          Typical response time: <span className="font-semibold text-slate-600">24–48 hours</span>
        </p>
      </motion.div>

      {/* Divider */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="w-32 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent my-8"
      />

      {/* Details */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.4 }}
        className="grid grid-cols-3 gap-6 mb-10 w-full max-w-sm"
      >
        {[
          { icon: '📧', label: 'Email Sent', value: 'Confirmed' },
          { icon: '⏱️', label: 'Response', value: '24-48hrs' },
          { icon: '🌍', label: 'Coverage', value: 'Worldwide' },
        ].map((item) => (
          <div
            key={item.label}
            className="flex flex-col items-center gap-1 p-3 bg-slate-50 rounded-xl border border-slate-100"
          >
            <span className="text-xl mb-1">{item.icon}</span>
            <p className="text-xs font-bold text-slate-800">{item.value}</p>
            <p className="text-xs text-slate-400">{item.label}</p>
          </div>
        ))}
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7, duration: 0.4 }}
        className="flex flex-col items-center gap-4"
      >
        <Button
          onClick={resetBuilder}
          size="lg"
          className="gap-2 min-w-56 shadow-xl shadow-amber-500/25"
        >
          <RotateCcw size={18} />
          Build Another Product
        </Button>
        <p className="text-xs text-slate-400">
          Have another product in mind? Start a new inquiry!
        </p>
      </motion.div>
    </motion.div>
  );
}
